import { ApiError, isForbidden } from '@cwos/api-client';
import type { alerts as AlertsNS } from '@cwos/api-client';
import { ATTENTION_STATUSES, sortAttentionAlerts } from '../shell/attention.js';
import { authorityGate, authorityGateAll, type AuthorityGate } from '../authorityGate.js';
import type { PermissionsState } from '../permissions.js';
import { dataHealthCaveat, type DataHealthCheck } from '../dataHealth.js';

type Alert = AlertsNS.Alert;

/**
 * WHAT THE TRIAGE QUEUE IS ENTITLED TO SAY, AND WHAT IT WAS SAYING.
 *
 * The screen asked `alerts.list({ limit: 100 })` — no `status`, no `severity` —
 * and then did both filters in the browser. Three things followed from that one
 * line, and the third is the serious one.
 *
 *   1. RESOLVED ALERTS ATE THE BUDGET. `listAlerts` orders `created_at desc`
 *      with no default status filter (alerts/service.ts:629), and a resolved
 *      alert is a row like any other. An organisation that has been running for
 *      a month has far more resolved alerts than open ones, so the 100 rows
 *      that came back could be a hundred settled items and the open ones were
 *      simply below the cut.
 *
 *   2. THE SEVERITY TABS WERE COSMETIC. Choosing "critical" filtered the same
 *      hundred rows, so a critical alert outside them stayed invisible and the
 *      tab said there were none.
 *
 *   3. AND THEN THE SCREEN PRINTED THE ALL-CLEAR OVER IT. Zero unresolved rows
 *      in the page rendered `<SuccessEmpty>` — "Nothing urgent is being missed
 *      — and when this says so, you can trust it." That sentence is the
 *      strongest claim this product makes and it was being produced by a
 *      truncated read. The header badge meanwhile counts from `total`, which is
 *      `countAlertsByStatus` over the SAME filter and is NOT capped, so the bell
 *      said 23 while the screen said nothing was being missed.
 *
 * THE SHAPE OF THE FIX. `listAlertsQuerySchema.status` is ONE status, not a
 * set, and "needs attention" is four of them (`ATTENTION_STATUSES`). So the
 * queue asks four server-side-filtered questions instead of one unfiltered one:
 * every row that comes back is unresolved, nothing settled is competing for the
 * cap, and `severity` rides along on each so the tabs narrow at the server too.
 *
 * AND THE COUNT COMES FROM THE SERVER, NOT FROM THE PAGE. Each response carries
 * `total` — counts by status, computed from the same `AlertFilter` as the rows
 * and unbounded by `limit`/`offset` (service.ts:655). That is the only figure
 * on this screen entitled to decide whether the queue is empty, because it is
 * the only one the cap cannot bind. Everything below exists so that the
 * all-clear is drawn from it and from a COMPLETE set of reads — never from a
 * page, and never from a set of reads with a hole in it.
 */

/** The cap this screen asks for per status, named so the summary can declare it. */
export const ALERT_PAGE_SIZE = 100;

/** The four statuses that mean "unresolved", as the shell already spells them. */
export const QUEUE_STATUSES = ATTENTION_STATUSES;

/**
 * One `(status, page)` read, reduced to what the queue decision needs.
 *
 * `alerts === undefined` is the load-bearing member: it is how a read that has
 * not answered — pending, failed, or never fired — is told apart from one that
 * answered with nothing. `?? []` at this boundary is the defect this whole file
 * exists downstream of.
 */
export interface StatusPageRead {
  status: string;
  /** Zero-based page. Only page 0 carries a total worth summing — see {@link queueAnswer}. */
  page: number;
  alerts: readonly Alert[] | undefined;
  /**
   * The server's count for THIS status under the same filters, or `undefined`
   * when the read has not answered. Not the page length.
   */
  statusTotal: number | undefined;
  isError: boolean;
  error: unknown;
  /**
   * PH-ATT-003. The server's own fact about the SCOPE this particular read
   * answered under — `undefined` when the read has not answered. Every page
   * of every status shares one caller and therefore one scope, so this is
   * the same value on every read that has one; {@link queueAnswer} reads it
   * off whichever answered read it finds first rather than re-deriving it.
   */
  organizationWide: boolean | undefined;
}

/**
 * The server's count for one status, out of the `total` summary it sent.
 *
 * ABSENT MEANS ZERO HERE, and it is worth saying why, because everywhere else
 * in this app absent means unknown. `countAlertsByStatus` is a `GROUP BY
 * status` over the filtered set: a status with no rows produces no row in the
 * result, so a missing entry is the server having counted and found none. The
 * unknown case is the one above it — no response at all — and it is answered
 * `undefined` before this ever looks at the array.
 */
export function statusTotalOf(
  result: { total: readonly { status: string; count: number }[] } | undefined,
  status: string,
): number | undefined {
  if (!result) return undefined;
  return result.total.find((t) => t.status === status)?.count ?? 0;
}

/**
 * What the queue may render.
 *
 * `partial` is a state this screen did not have and needed most: four reads,
 * some answered and some not. The rows that arrived are real and worth showing
 * — a supervisor should see the critical item that DID load — but no total may
 * be stated over them and the all-clear is unreachable, because the missing
 * read is exactly where the thing being missed would be.
 */
export type QueueAnswer =
  /** Nothing has answered yet. */
  | { kind: 'loading' }
  /** The server said 403. An answer, never a retry. */
  | { kind: 'refused' }
  /** Every read failed. Retryable, and says nothing about the queue. */
  | { kind: 'failed' }
  /** Some reads answered and some did not: rows, but no count and no all-clear. */
  | { kind: 'partial'; rows: Alert[]; missing: string[] }
  /**
   * Every read answered.
   *
   * `total` is the server's unresolved count under the current severity filter,
   * uncapped. `more` is true when the cap bound the read — the one condition
   * under which a shorter list must not be presented as the whole queue.
   *
   * `organizationWide` (PH-ATT-003) is the server's own fact about the scope
   * this answer covers — `undefined` only if every answered read somehow
   * omitted it (should not happen once the API carries the field; kept
   * `undefined`-able so an old cached response cannot be misread as `true`).
   */
  | {
      kind: 'answered';
      rows: Alert[];
      total: number;
      more: boolean;
      organizationWide: boolean | undefined;
    };

/**
 * Merge the per-status pages into the queue, then decide what may be said.
 *
 * ORDER OF THE BRANCHES IS THE CONTRACT. Refusal first — a permission that has
 * been taken away is an answer about now, not a failure — then the
 * nothing-arrived cases, then the hole-in-the-set case, and only at the bottom,
 * where every read is present, a total.
 */
export function queueAnswer(reads: readonly StatusPageRead[]): QueueAnswer {
  if (reads.length === 0) return { kind: 'loading' };
  // Every page of every status is the same route with the same two permission
  // keys, so one 403 is the route's answer for all of them.
  if (reads.some((r) => r.isError && isForbidden(r.error))) return { kind: 'refused' };

  const answered = reads.filter((r) => r.alerts !== undefined);
  if (answered.length === 0) {
    return reads.every((r) => r.isError) ? { kind: 'failed' } : { kind: 'loading' };
  }

  const rows = queueRows(answered.flatMap((r) => [...(r.alerts ?? [])]));

  if (answered.length < reads.length) {
    const missing = [...new Set(reads.filter((r) => r.alerts === undefined).map((r) => r.status))];
    return { kind: 'partial', rows, missing };
  }

  // Every page of a status carries the SAME total — it is a count over the
  // filter, and `limit`/`offset` are not part of the filter — so summing over
  // every page would multiply the queue by the page count. Page 0 is the one
  // that always exists.
  let total = 0;
  for (const r of reads) {
    if (r.page === 0) total += r.statusTotal ?? 0;
  }
  // Every read shares one caller and one scope (PH-ATT-003), so the first
  // answered read that carries the fact is as good as any other — this is
  // NOT an aggregation, it is reading the same value off whichever read
  // happened to be first.
  const organizationWide = answered.find((r) => r.organizationWide !== undefined)?.organizationWide;
  return { kind: 'answered', rows, total, more: rows.length < total, organizationWide };
}

/**
 * WHOSE QUEUE THIS IS, for `FilterSummary` — read off the server's own
 * `organizationWide` fact (PH-ATT-003), never re-derived from the caller's
 * permissions.
 *
 * Mirrors `insightsLogic.ts`'s identical choice for `Insights.tsx`: `false`
 * names the caseload directly ("the people you supervise"); `true` OR
 * `undefined` (not yet answered) keeps the organization's name, because an
 * unanswered scope is not evidence of narrowing either — only a definite
 * `false` may say so. `undefined` is why `queueScopeName` and
 * {@link queueScopeNarrowedBy} are two functions rather than one deriving both
 * from a single ternary: the label defaults optimistically while the answer
 * is pending, but `narrowedBy` below must NOT default at all in that window.
 */
export function queueScopeName(organizationWide: boolean | undefined, orgName: string): string {
  return organizationWide === false ? 'the people you supervise' : orgName;
}

/**
 * `FilterSummary`'s third `narrowedBy` value, applied to the same fact.
 * `NARROWING_UNKNOWN`-shaped by the caller (this returns the plain
 * `boolean | undefined` the caller already has organized by; `Attention.tsx`
 * turns `undefined` into `NARROWING_UNKNOWN` at the render site, the same way
 * `Insights.tsx` does) — kept here as the one place that states the RULE:
 * unanswered is not "not narrowed", so the completeness promise may not be
 * made until the scope is actually known.
 */
export function queueScopeNarrowedBy(
  organizationWide: boolean | undefined,
): 'unknown' | 'narrowed' | 'organization' {
  if (organizationWide === undefined) return 'unknown';
  return organizationWide ? 'organization' : 'narrowed';
}

/**
 * THE ALL-CLEAR SENTENCE'S ONE CAVEAT, COMBINING BOTH HALVES THIS QUEUE MUST
 * ANSWER FOR BEFORE IT MAY SAY "you can trust it".
 *
 * PH-ATT-001 (data health) and PH-ATT-003 (alert-scope reach) are two
 * different reasons the same two trust clauses may not be printed, and a
 * caller can be short EITHER, both, or neither. `undefined` here means both
 * came back favourable and the plain, unqualified sentence may render.
 */
export function attentionTrustCaveat(
  dataHealthCheck: DataHealthCheck,
  organizationWide: boolean | undefined,
): string | undefined {
  const dataCaveat = dataHealthCaveat(dataHealthCheck);
  const scopeCaveat =
    organizationWide === false
      ? 'this only covers the people you supervise — someone with organization-wide access may see more'
      : organizationWide === undefined
        ? 'we could not confirm whether this covers your whole organization'
        : undefined;
  if (dataCaveat && scopeCaveat) return `${dataCaveat} And ${scopeCaveat}.`;
  if (dataCaveat) return dataCaveat;
  if (scopeCaveat) return `${scopeCaveat}.`;
  return undefined;
}

/**
 * The rows, deduplicated and ordered the way the bell's panel orders them.
 *
 * DEDUPLICATION IS NOT DEFENSIVENESS. The four reads are four round trips, and
 * an alert acknowledged between the `open` request and the `acknowledged` one
 * is genuinely returned by both. Keyed on `id`, first occurrence wins — which
 * after `sortAttentionAlerts` is the higher-severity, newer copy of the same
 * row.
 *
 * The sort itself is `shell/attention.ts`'s, not a second one: the badge, the
 * bell panel and this screen are three renderings of one queue, and a queue
 * that orders itself differently depending on where you look at it is two
 * queues.
 */
export function queueRows(rows: readonly Alert[]): Alert[] {
  const seen = new Set<string>();
  const out: Alert[] = [];
  for (const a of sortAttentionAlerts(rows)) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

/**
 * How many pages a status is worth asking for, given what its first page said.
 *
 * Page 0 always fires. Page n>0 fires only once page 0 has come back and its
 * `total` says there is something at that offset — so a queue of six alerts
 * makes four requests however many times the reader presses "Show more", and a
 * status whose read FAILED is never paged over a total nobody has.
 */
export function pageIsWorthAsking(page: number, statusTotal: number | undefined): boolean {
  if (page === 0) return true;
  if (statusTotal === undefined) return false;
  return statusTotal > page * ALERT_PAGE_SIZE;
}

/**
 * The sentence under a truncated queue.
 *
 * Says what is shown and what the server counted, and never subtracts them into
 * "23 hidden" — the two figures are read at different moments and the
 * difference is not a set anybody can point at.
 */
export function truncationNote(shown: number, total: number): string {
  return (
    `Showing ${shown} of ${total} unresolved items. The rest are not on this screen yet — ` +
    'do not read the bottom of this list as the bottom of the queue.'
  );
}

/**
 * The sentence over a queue with a hole in it.
 *
 * Named after the statuses that did not answer, because "some alerts could not
 * be read" leaves a reader unable to judge what they are missing, and an
 * escalated item nobody can see is a different problem from an acknowledged one.
 */
export function partialNote(missing: readonly string[]): string {
  const which = missing.length === 0 ? 'part of the queue' : missing.join(', ');
  return (
    `We could not read the ${which} items just now, so this list is incomplete and there is ` +
    'no total to give you. Nothing here says the queue is clear.'
  );
}

// ---------------------------------------------------------------------------
// THE VERBS THE QUEUE ALREADY HAD ON THE SERVER
// ---------------------------------------------------------------------------
//
// `alerts.assign`, `alerts.resolve` and `alerts.escalate` shipped in
// `@cwos/api-client` with ZERO call sites. The only verb any screen offered was
// `acknowledge`, which moves an alert from `open` to `acknowledged` and no
// further — so nothing in the product could take an alert OUT of the attention
// queue, and the header badge (`deriveAttentionCount`, which counts the four
// unresolved statuses) could only ever grow. A supervisor who dealt with an
// alert had no way to say so.
//
// Two collapses came with it:
//
//   * FOUR STATUSES, ONE BOOLEAN. The screen rendered
//     `acknowledged={a.status === 'acknowledged'}`, so `open`, `assigned` and
//     `escalated` were the same row — three different obligations drawn
//     identically. An alert somebody had already taken looked like one nobody
//     had touched.
//   * `escalated` HAD NO LABEL ANYWHERE. It is a real status the rules engine
//     writes (`rules/actions.ts` escalates an unresolved alert past its
//     configured period and bumps its severity), and no surface in the admin
//     app named it. The strongest signal the queue produces was invisible.
//
// ---------------------------------------------------------------------------
// WHY THE OFFERS ARE DERIVED AND NOT LISTED PER BUTTON
// ---------------------------------------------------------------------------
// The brief for this work is blunt: do not offer a control the server will
// refuse. The server's rules live in `alerts/service.ts` and are not symmetric
// — `acknowledge` is `open`-only, `assign` and `resolve` both refuse `open`
// ("acknowledge first") and `resolved`, `escalate` refuses only `resolved` —
// so a screen that guessed would offer a button that answers 409. These
// predicates are transcribed from that file, one per verb, and
// `attention-verbs.test.ts` re-derives them from the same statuses so a change
// on either side is a red test rather than a dead button.

/** The four lifecycle verbs, exactly as the API client spells them. */
export type AlertVerb = 'acknowledge' | 'assign' | 'resolve' | 'escalate';

export const ALERT_VERB_LABEL: Readonly<Record<AlertVerb, string>> = Object.freeze({
  acknowledge: 'Got it',
  assign: 'Assign',
  resolve: 'Resolve',
  escalate: 'Escalate',
});

/**
 * The status vocabulary, said the way a supervisor would say it.
 *
 * `resolved` is here even though the queue never shows one: a row can settle
 * between the read and the render, and the label is also what the detail pane
 * shows immediately after somebody resolves an alert.
 */
export const ALERT_STATUS_LABEL: Readonly<Record<string, string>> = Object.freeze({
  open: 'Open',
  acknowledged: 'Acknowledged',
  assigned: 'Assigned',
  escalated: 'Escalated',
  resolved: 'Resolved',
});

/** What each status MEANS — one sentence, because a badge word is not an answer. */
export const ALERT_STATUS_MEANING: Readonly<Record<string, string>> = Object.freeze({
  open: 'Nobody has taken this yet.',
  acknowledged: 'Somebody has taken ownership. It is still unresolved.',
  assigned: 'It belongs to a named person and is still unresolved.',
  escalated: 'It went past its escalation period unresolved, so it was raised.',
  resolved: 'It is closed, with the resolution recorded against it.',
});

/**
 * The label for a status, or the raw token for one this build does not know.
 *
 * NOT `?? 'Open'`. `alerts.Alert.status` is typed `string`, and a server that
 * grows a sixth status would otherwise have it drawn as the one status that
 * means "nobody has touched this" — the most consequential possible wrong
 * answer on this screen.
 */
export function alertStatusLabel(status: string): string {
  return ALERT_STATUS_LABEL[status] ?? status;
}

/**
 * The verbs `alerts/service.ts` will accept from `status`, in the order a
 * triage screen should offer them.
 *
 * A STATUS THIS BUILD DOES NOT KNOW YIELDS NO VERBS, and that is the
 * unknown/absent distinction rather than caution: we cannot say what the server
 * would accept from a status we have never heard of, and every offer would be a
 * guess that costs somebody a 409. {@link unknownStatusNote} is the sentence
 * that goes with it, so the pane says "we do not know" instead of showing an
 * empty row of buttons.
 */
export function alertVerbsFor(status: string): AlertVerb[] {
  if (!(status in ALERT_STATUS_LABEL)) return [];
  const verbs: AlertVerb[] = [];
  // service.ts:309 — `acknowledgeAlert` refuses every status but `open`.
  if (status === 'open') verbs.push('acknowledge');
  // service.ts:417,420 — `assignAlert` refuses `open` ("acknowledge first")
  // and `resolved`.
  if (status !== 'open' && status !== 'resolved') verbs.push('assign');
  // service.ts:490,493 — `resolveAlert` refuses `resolved` and `open`.
  if (status !== 'open' && status !== 'resolved') verbs.push('resolve');
  // service.ts:561 — `escalateAlert` refuses only `resolved`. It does NOT
  // require an acknowledgement first; an open alert escalates directly.
  if (status !== 'resolved') verbs.push('escalate');
  return verbs;
}

/**
 * The sentence for an alert that has settled, or `null` while it has not.
 *
 * THE DATA MODEL IS WHY THERE IS NO EDIT HERE. `resolveAlert` refuses a second
 * resolution ("Alert is already resolved"), there is no reopen route, and
 * migration 1757200000000 revoked DELETE on `alert_transition` and
 * `alert_acknowledgement` on the grounds that "a trail that the traceable party
 * can delete is not a trail". So a resolution is a durable named act, and an
 * "edit resolution" control would be a promise the schema and the service both
 * refuse to keep.
 */
export function settledNote(status: string): string | null {
  if (status !== 'resolved') return null;
  return (
    'This alert is resolved. The resolution is part of its permanent record — there is no ' +
    'route that edits or reopens it, so anything further belongs on a new alert.'
  );
}

/** The sentence for a status this build has no vocabulary for. */
export function unknownStatusNote(status: string): string | null {
  if (status in ALERT_STATUS_LABEL) return null;
  return (
    `This alert is in a state this screen does not know how to act on (“${status}”). No action ` +
    'is offered because we cannot tell which ones would be accepted, not because none would be.'
  );
}

/**
 * What the server said when a lifecycle write did not land.
 *
 * A 409 IS AN ANSWER, NOT A FAILURE, and the two need different words. The
 * lifecycle refusals — "Cannot assign an unacknowledged alert", "Alert is
 * already resolved" — mean the row moved between the read and the write, which
 * on a queue several people work is ordinary. Telling somebody to try again is
 * wrong; telling them the alert changed and showing them the server's own
 * sentence is right.
 */
export interface VerbFailure {
  /** True when the server refused on state, so the queue should be re-read. */
  readonly stale: boolean;
  /** The server's sentence, or '' when it sent none. Never invented. */
  readonly message: string;
}

export function verbFailure(err: unknown): VerbFailure {
  const status = err instanceof ApiError ? err.status : null;
  return {
    stale: status === 409,
    message: err instanceof Error ? err.message : '',
  };
}

/**
 * WHAT IS STILL TRUE after each verb fails to land.
 *
 * One clause per verb rather than one sentence for all four, because "this is
 * still open" is the residue of a failed ACKNOWLEDGEMENT and is false of a
 * failed resolve on an alert that was already acknowledged. A queue whose
 * failure message describes the wrong state is a failure message somebody
 * learns to ignore.
 */
const VERB_FAILURE_RESIDUE: Readonly<Record<AlertVerb, string>> = Object.freeze({
  acknowledge: 'this is still open and still waiting for someone',
  assign: 'it still belongs to whoever it belonged to before',
  resolve: 'this is still unresolved and still waiting for someone',
  escalate: 'this has not been raised',
});

export function verbFailureSentence(verb: AlertVerb, failure: VerbFailure): string {
  const act = ALERT_VERB_LABEL[verb];
  return failure.stale
    ? `Somebody moved this alert before “${act}” landed, so nothing was written. The ` +
        `queue is being re-read — pressing again would repeat this answer. ${failure.message}`
    : `That “${act}” didn’t save — ${VERB_FAILURE_RESIDUE[verb]}. ${failure.message}`;
}

// ---------------------------------------------------------------------------
// THE TWO DOORS THIS SCREEN OPENS, AS PURE FUNCTIONS
// ---------------------------------------------------------------------------
//
// WHY THEY ARE HERE AND NOT INLINE IN THE SCREEN. Both gates resolve from the
// same `/auth/me` read that the screen's own `alerts:read` gate resolves from,
// so from a MOUNT their `unresolved` and `unknown` states are unreachable: while
// the permission set is in flight the screen renders a skeleton and never gets
// as far as the detail pane. Written inline they would read as covered by a
// component test and be exercised by nothing — the defence-in-depth failure the
// lane contract names ("the only proof a guard works is a fixture where that
// guard is the sole thing in the way").
//
// `docs/intelligence/audit/UNKNOWN-IS-NOT-DENIED-FRONTEND.md` reached the same
// conclusion about `Visits.tsx:206` and moved it to
// `visitLogic.clientNameResolver` for the same reason. This is the same move.

/**
 * The keys `alerts/routes.ts` charges on every lifecycle write.
 *
 * `directory:read` is not decoration: the write routes each answer with the
 * full `AlertDetailView`, so the file's own docblock adds the directory gate to
 * the writes as well ("a caller who cannot read a record must not be able to
 * obtain it by writing to it").
 *
 * IT IS ALSO, AT THIS SITE, ALREADY ESTABLISHED. `mayReadAlerts` charges
 * `alerts:read` AND `directory:read`, so a reader who reached the queue at all
 * holds the second key — which means the conjunction cannot be isolated from a
 * mount of `/attention`, and the unit tests below are where it IS proved.
 * Recorded rather than claimed as covered.
 */
export const ALERT_WRITE_PERMISSIONS = ['alerts:write', 'directory:read'] as const;

/** May this reader act on an alert, and if not, why not. */
export function alertWriteGate(
  state: Pick<PermissionsState, 'has' | 'isLoading' | 'isError'>,
): AuthorityGate {
  return authorityGateAll(state, ALERT_WRITE_PERMISSIONS);
}

/**
 * May this reader see the list of people to assign an alert TO.
 *
 * A SEPARATE KEY FROM THE WRITE, deliberately. `GET /users` charges
 * `user:read`, and `alerts:write` is seeded to `supervisor`, which does not
 * necessarily hold it. Folding the two together would take "assign" away from a
 * supervisor who can perfectly well take an alert themselves.
 */
export function alertRosterGate(
  state: Pick<PermissionsState, 'has' | 'isLoading' | 'isError'>,
): AuthorityGate {
  return authorityGate(state, 'user:read');
}
